'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { organizationPath } from '@kit/shared/handles';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';

import { DancerSection } from './dancer-section';
import { isAwardExperience } from './experience-partition';
import type { DancerExperience as Experience } from './types';

interface Props {
  experiences: Experience[];
}

/**
 * Awards section — the AWARDS bucket of the experience credits
 * (`type IN ('award','competition')` OR a non-empty placement; see
 * `experience-partition.ts`). Distinct from the Experience timeline: the
 * placement/rank is the headline emphasis here (it's the point of an award),
 * with the competition/organization as the secondary line — logo-left and
 * linked when the org is public.
 *
 * Self-hides (`return null`) when the bucket is empty, matching the
 * single-scroll silent-omission contract.
 */
export function DancerAwards({ experiences }: Props) {
  const { t } = useTranslation();

  const sorted = sortAwards(experiences.filter(isAwardExperience)).filter(
    hasAwardContent,
  );
  if (sorted.length === 0) return null;

  return (
    <DancerSection
      eyebrow={t('dancer:publicView.awards.eyebrow')}
      title={t('dancer:publicView.awards.title')}
      count={sorted.length}
    >
      <ol className="divide-foreground/10 divide-y">
        {sorted.map((exp) => {
          const dateLabel = formatYear(exp);
          const org = exp.organization;
          const orgName = org?.name ?? exp.organization_manual ?? '';
          // Headline = the achievement: placement first, then rank, then the
          // award/competition title. Secondary line = competition/org + role.
          const headline =
            exp.placement?.trim() ||
            exp.rank_title?.trim() ||
            exp.title?.trim() ||
            orgName ||
            '—';
          const secondary = composeAwardSecondary(exp, orgName, headline);
          const initials = orgName.trim().slice(0, 2).toUpperCase() || '★';

          return (
            <li
              key={exp.id}
              className="grid grid-cols-12 items-start gap-x-4 gap-y-2 py-6 sm:py-7"
            >
              <div className="col-span-2 sm:col-span-1">
                <Avatar className="h-10 w-10">
                  {org?.logoUrl && (
                    <AvatarImage src={org.logoUrl} alt={`${org.name} logo`} />
                  )}
                  <AvatarFallback className="text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="text-muted-foreground/80 col-span-10 font-mono text-[11px] tracking-[0.18em] uppercase tabular-nums sm:col-span-3 sm:pt-2">
                {dateLabel}
              </div>

              <div className="col-span-12 sm:col-span-8">
                <div className="font-heading text-foreground text-xl leading-tight tracking-[-0.01em] sm:text-2xl">
                  {headline}
                </div>
                {secondary && (
                  <div className="text-muted-foreground mt-1 text-base leading-relaxed">
                    {org ? (
                      <Link
                        href={organizationPath(org.handle)}
                        className="decoration-muted-foreground/40 hover:text-foreground hover:decoration-foreground/60 underline underline-offset-2 transition-colors"
                      >
                        {secondary}
                      </Link>
                    ) : (
                      secondary
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </DancerSection>
  );
}

/** A row is worth rendering only if it carries an achievement signal beyond
 * its date — a placement, rank, title, or named org/competition. */
function hasAwardContent(exp: Experience): boolean {
  return Boolean(
    exp.placement?.trim() ||
    exp.rank_title?.trim() ||
    exp.title?.trim() ||
    exp.organization?.name?.trim() ||
    exp.organization_manual?.trim(),
  );
}

function sortAwards(items: Experience[]): Experience[] {
  return [...items].sort((a, b) => {
    const ay = a.start_date ?? '';
    const by = b.start_date ?? '';
    return by.localeCompare(ay);
  });
}

function formatYear(exp: Experience): string {
  return exp.start_date ? exp.start_date.slice(0, 4) : '—';
}

/** Secondary line: the competition/org name + the award title when the title
 * wasn't already promoted to the headline. Returns '' when there's nothing to
 * add beyond the headline. */
function composeAwardSecondary(
  exp: Experience,
  orgName: string,
  headline: string,
): string {
  const parts: string[] = [];
  if (orgName) parts.push(orgName);
  const title = exp.title?.trim();
  if (title && title !== headline) parts.push(title);
  return parts.join(' · ');
}
